class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.content = this.summary.nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  open() {
    if (this.mainDetailsToggle.open) return;
    this.mainDetailsToggle.setAttribute('open', true);
    this.summary.setAttribute('aria-expanded', true);
    if (!this.animations) this.animations = this.content.getAnimations();
    this.animations.forEach((animation) => animation.play());
  }

  close() {
    // Don't close if already closing or already closed
    if (!this.mainDetailsToggle.open || this.mainDetailsToggle.classList.contains('is-closing')) return;

    // Add closing class for fade-out animation
    this.mainDetailsToggle.classList.add('is-closing');

    // Wait for animation to complete before removing open attribute
    setTimeout(() => {
      this.mainDetailsToggle.removeAttribute('open');
      this.mainDetailsToggle.classList.remove('is-closing');
      this.summary.setAttribute('aria-expanded', false);
    }, 500); // Match the CSS transition duration (0.5s)

    if (!this.animations) this.animations = this.content.getAnimations();
    this.animations.forEach((animation) => animation.cancel());
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  static overlay = null;

  static getOverlay() {
    if (!HeaderMenu.overlay) {
      HeaderMenu.overlay = document.createElement('div');
      HeaderMenu.overlay.className = 'mega-menu-overlay';
      HeaderMenu.overlay.setAttribute('data-mega-menu-overlay', '');
      document.body.appendChild(HeaderMenu.overlay);
    }
    return HeaderMenu.overlay;
  }

  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.mouseOverDelay = 230;
    this.mq = window.matchMedia('screen and (pointer: coarse)');

    // Check if this is a level 2 details element (should always stay open)
    this.isLevel2 = this.mainDetailsToggle.classList.contains('mega-menu__details--level-2');

    // Get or create overlay element for mega menu
    if (!this.isLevel2) {
      this.overlay = HeaderMenu.getOverlay();
      this.overlay.addEventListener('click', () => this.close());
    }

    this.addEventListener('mouseover', this.handleHover.bind(this));
    this.addEventListener('mouseout', this.handleHover.bind(this));
    this.summary.addEventListener('click', this.onClick.bind(this));
  }

  get trigger() {
    return this.mq.matches ? 'click' : this.getAttribute('data-toggle-trigger');
  }

  onClick() {
    if (this.trigger === 'hover' && this.mainDetailsToggle.open && this.summary.hasAttribute('data-follows-link')) {
      return (window.location.href = this.summary.getAttribute('data-follows-link'));
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  handleHover(event) {
    // Don't handle hover for level 2 - they should always stay open
    if (this.isLevel2) return;

    if (this.trigger === 'click') return;
    if (event.type === 'mouseover') {
      clearTimeout(this.hoverTimeout);
      this.open();
    } else if (event.type === 'mouseout') {
      this.hoverTimeout = setTimeout(() => this.close(), this.mouseOverDelay);
    }
  }

  open() {
    // Don't toggle level 2 - they should always stay open
    if (this.isLevel2) return;
    super.open();
    // Show overlay
    if (this.overlay) {
      this.overlay.classList.add('is-visible');
    }
  }

  close() {
    // Don't close level 2 - they should always stay open
    if (this.isLevel2) return;
    super.close();
    // Hide overlay
    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }
  }
}

customElements.define('header-menu', HeaderMenu);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.handleItemEvent.bind(this));
    this.addEventListener('mouseover', this.handleItemEvent.bind(this));

    // Handle level 3 hover to highlight parent level 2
    this.addEventListener('mouseover', this.handleLevel3Hover.bind(this));
    this.addEventListener('mouseout', this.handleLevel3Hover.bind(this));
  }

  handleItemEvent(event) {
    if (
      !['SUMMARY', 'A'].some(
        (tagName) => event.target.tagName === tagName || event.target.closest(tagName)?.tagName === tagName
      )
    ) {
      return;
    }
    let li = event.target.closest('li');
    let linkLevel = li.getAttribute('data-link-level');
    let linkHandle = li.getAttribute('data-link-handle');

    publish(PUB_SUB_EVENTS.megaMenuItemChange, {
      link: li,
      linkLevel,
      linkHandle,
    });
  }

  handleLevel3Hover(event) {
    // Check if hovering over the ul that contains level 3 items
    const ul = event.target.closest('.mega-menu__details--level-2 .mega-menu__content ul');

    if (ul) {
      // Find the parent level 2 details element
      const level2Details = ul.closest('.mega-menu__details--level-2');

      if (level2Details) {
        if (event.type === 'mouseover') {
          level2Details.classList.add('level-3-hovered');
        } else if (event.type === 'mouseout') {
          // Check if we're still hovering over the ul
          setTimeout(() => {
            const stillHovering = level2Details.querySelector('.mega-menu__content ul:hover');
            if (!stillHovering) {
              level2Details.classList.remove('level-3-hovered');
            }
          }, 10);
        }
      }
    }
  }
}

customElements.define('mega-menu', MegaMenu);

class MegaMenuPromotions extends HTMLElement {
  constructor() {
    super();

    this.allPromotions = this.querySelectorAll('.promotion');
    this.previousHandle = '';
    this.previousLevel = 0;
  }

  itemChangeUnsubscriber = undefined;

  connectedCallback() {
    this.itemChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.megaMenuItemChange, (event) => {
      this.onItemChange(event);
    });
  }

  disconnectedCallback() {
    if (this.itemChangeUnsubscriber) {
      this.itemChangeUnsubscriber();
    }
  }

  setPromotions(level, handle) {
    let promotions = this.querySelectorAll(`[data-link-level="${level}"][data-link-handle="${handle}"]`);

    if (promotions.length) {
      this.previousLevel = level;
      this.allPromotions.forEach((p) => p.classList.add('hidden'));
      promotions.forEach((p) => p.classList.remove('hidden'));
    }

    return promotions.length ? promotions : null;
  }

  onItemChange(event) {
    let currentLevel = Number(event.linkLevel);
    let promotions = this.setPromotions(currentLevel, event.linkHandle);

    if (!promotions && currentLevel <= this.previousLevel) {
      let parentLink = event.link.parentElement.closest('li');

      if (!parentLink) {
        this.allPromotions.forEach((p) => p.classList.add('hidden'));
        return;
      }

      let parentLinkLevel = Number(parentLink.getAttribute('data-link-level'));
      let parentLinkHandle = parentLink.getAttribute('data-link-handle');

      this.setPromotions(parentLinkLevel, parentLinkHandle);
    }
  }
}

customElements.define('mega-menu-promotions', MegaMenuPromotions);
